import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Grid,
  IconButton,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  Box,
  Breadcrumbs,
  Chip,
} from '@mui/material';
import PlanName from './PlanName';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

type PlanType = 'starter' | 'smarter' | 'pro';
type BillingCycle = 'Monthly' | 'Bi-Annual' | 'Annual';

interface SelectPlanModalProps {
  open: boolean;
  selectedBillingCycle: BillingCycle;
  handleClose: () => void;
  handleSelectPlan: (plan: PlanType) => void;
  hanldeBillingCycle: (plan: BillingCycle) => void;
}

const plans = [
  {
    name: 'Smarter',
    value: 'smarter',
    disclaimerText:
      'Any size team, unlimited job <br/> posts, hiring automated',
    planHeading: 'Everything in Starter +',
    buttonText: 'Be smarter',
    plans: [
      { billingCycle: 'Monthly', price: '49' },
      { billingCycle: 'Bi-Annual', price: '44', actualPrice: '10' },
      { billingCycle: 'Annual', price: '39', actualPrice: '20' },
    ],
    planFeatures: [
      'Unlimited job posts',
      'Full cycle hiring automation',
      'Hiring analytics',
      'Custom branded QR & hiring graphics',
      'One on one account onboarding',
      'Unlimited support',
    ],
  },
  {
    name: 'Pro',
    value: 'pro',
    planHeading: 'Everything in Smarter +',
    disclaimerText: 'Everything you’ll need <br/>in hiring, in one place',
    buttonText: 'Get Pro',

    plans: [
      { billingCycle: 'Monthly', price: '99' },
      { billingCycle: 'Bi-Annual', price: '89', actualPrice: '10' },
      { billingCycle: 'Annual', price: '79', actualPrice: '20' },
    ],
    planFeatures: [
      'Digital new hire onboarding',
      'Customized company paperwork',
      'Hiring document storage',
      'Teams management',
      'Multi-user access',
      'Manager permissions & controls',
    ],
  },
];

const SelectPlanModal: React.FC<SelectPlanModalProps> = ({
  open,
  handleClose,
  handleSelectPlan,
  hanldeBillingCycle,
  selectedBillingCycle,
}) => {
  const [selectedPlan, setSelectedPlan] = React.useState<PlanType | null>(null);

  const handlePlanSelect = (plan: PlanType) => {
    setSelectedPlan(plan);
  };

  const handleSubmit = () => {
    if (selectedPlan) {
      handleSelectPlan(selectedPlan);
    }
    handleClose();
  };
  const calclulatedPrice = (actualPrice?: string) => {
    if (selectedBillingCycle === 'Monthly') {
      return actualPrice;
    }
    if (selectedBillingCycle === 'Bi-Annual' && actualPrice) {
      return Number(actualPrice) * 6;
    }
    if (selectedBillingCycle === 'Annual' && actualPrice) {
      return Number(actualPrice) * 12;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Breadcrumbs
            separator="/"
            aria-label="breadcrumb"
            sx={{
              // border: '1px solid gray',
              // borderRadius: '50px',
              alignItems: 'center',
              width: 'fit-content',
              // padding: '0 30px',
            }}
          >
            {plans[0].plans?.map(({ billingCycle }) => (
              <Button
                key={billingCycle}
                color={
                  selectedBillingCycle === billingCycle ? 'primary' : 'inherit'
                }
                onClick={() => hanldeBillingCycle(billingCycle as BillingCycle)}
                variant="contained"
                size="small"
              >
                {billingCycle}
                {selectedBillingCycle === billingCycle && (
                  <span
                    style={{
                      position: 'absolute',
                      bottom: '-5px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: '#ff5104',
                    }}
                  />
                )}
              </Button>
            ))}
          </Breadcrumbs>
          <Grid mt={2} container spacing={3} justifyContent="center">
            {plans.map((plan) => (
              <Grid item xs={12} sm={6} key={plan.value}>
                <Card
                  sx={{
                    borderRadius: '12px',
                    boxShadow:
                      selectedPlan === plan.value
                        ? '0px 4px 15px rgba(0, 123, 255, 0.3)'
                        : '0px 2px 10px rgba(0, 0, 0, 0.1)',
                    border:
                      selectedPlan === plan.value
                        ? '2px solid #007BFF'
                        : '2px solid transparent',
                    '&:hover': {
                      boxShadow: '0px 4px 20px rgba(0, 123, 255, 0.4)',
                      transform: 'scale(1.01)',
                      transition: 'all 0.3s ease-in-out',
                      cursor: 'pointer',
                    },
                    textAlign: 'center',
                    padding: '20px',
                    position: 'relative',
                  }}
                  onClick={() => handlePlanSelect(plan.value as PlanType)}
                >
                  <CardContent>
                    <PlanName planName={plan.name} />
                    <Typography
                      variant="h6"
                      color="gray"
                      fontWeight={600}
                      fontSize={20}
                      mt={3}
                      dangerouslySetInnerHTML={{
                        __html: plan.disclaimerText,
                      }}
                    />
                    <Box
                      justifyContent="center"
                      display="flex"
                      alignItems="center"
                      gap="10px"
                    >
                      <Typography
                        fontSize="50px"
                        variant="h1"
                        fontWeight="bold"
                      >
                        $
                        {
                          plan.plans?.find(
                            (p) => p.billingCycle === selectedBillingCycle,
                          )?.price
                        }
                      </Typography>
                      {plan.plans?.find(
                        (p) => p.billingCycle === selectedBillingCycle,
                      )?.actualPrice && (
                        <Chip
                          label={`Save ${
                            plan.plans?.find(
                              (p) => p.billingCycle === selectedBillingCycle,
                            )?.actualPrice
                          }%`}
                          sx={{
                            backgroundColor: '#FF5104',
                            color: '#FFFFFF',
                            fontSize: '12px',
                            height: '20px',
                            borderRadius: '4px',
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      fontSize="18px"
                      variant="body2"
                      color="text.secondary"
                    >
                      / mo billed{' '}
                      {
                        <b>
                          $
                          {calclulatedPrice(
                            plan.plans?.find(
                              (p) => p.billingCycle === selectedBillingCycle,
                            )?.price,
                          )}
                        </b>
                      }{' '}
                      {selectedBillingCycle}
                      {!selectedBillingCycle?.includes('ly') && 'ly'}
                    </Typography>
                    <Typography
                      variant="h6"
                      color="gray"
                      fontWeight={1000}
                      fontSize="18px"
                      mt={3}
                    >
                      {plan.planHeading}
                    </Typography>
                    <Box sx={{ textAlign: 'left', mt: 1 }}>
                      {plan.planFeatures.map((feature, index) => {
                        const isLast = index === plan.planFeatures.length - 1;
                        return (
                          <Box
                            margin={2}
                            display="flex"
                            alignItems="center"
                            key={index}
                            gap={3}
                          >
                            <CheckCircleIcon color="primary" />
                            <Typography color="gray">{feature}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', marginBottom: '20px' }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          color="secondary"
          sx={{ width: '200px', fontSize: '1rem', padding: '10px' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!selectedPlan}
          sx={{
            width: '200px',
            fontSize: '1rem',
            fontWeight: 'bold',
            padding: '10px',
          }}
        >
          Confirm Selection
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectPlanModal;
